/* PlayMix performance + a11y helpers.
   1. Hero video: starts as a poster image; the 2 MB MP4 loads only on desktop-width, non-data-saver
      connections, after the page has finished loading and the browser is idle.
   2. Microsoft Clarity loads on first interaction or 8 s after load (app embed is off).
   3. Carousel slides marked aria-hidden get `inert`, so their buttons/links leave the tab order. */
(() => {
  // ---- 1. deferred hero video ----
  const startVideos = () => {
    const conn = navigator.connection;
    if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;
    if (!matchMedia('(min-width: 750px)').matches) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.querySelectorAll('video[data-pm-defer-src]').forEach((v) => {
      v.src = v.dataset.pmDeferSrc;
      v.removeAttribute('data-pm-defer-src');
      v.autoplay = true;
      v.play().catch(() => {});
    });
  };
  const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 4000 }) : setTimeout(fn, 1500));
  if (document.readyState === 'complete') idle(startVideos);
  else addEventListener('load', () => idle(startVideos), { once: true });

  // ---- 2. Microsoft Clarity, delayed until first interaction (or 8 s after load) ----
  // Same snippet as the Clarity app embed (incl. Shopify consent), which is switched off.
  let clarityStarted = false;
  const startClarity = () => {
    if (clarityStarted) return;
    clarityStarted = true;
    ['scroll', 'pointerdown', 'keydown', 'touchstart'].forEach((ev) => removeEventListener(ev, startClarity));
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i + '?ref=shopify';
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
      if (c.Shopify && c.Shopify.loadFeatures) {
        c.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          if (error) return;
          c[a]('consentv2', {
            ad_Storage: c.Shopify.customerPrivacy.marketingAllowed() ? 'granted' : 'denied',
            analytics_Storage: c.Shopify.customerPrivacy.analyticsProcessingAllowed() ? 'granted' : 'denied',
            source: 101,
          });
        });
      }
      l.addEventListener('visitorConsentCollected', (e) => {
        c[a]('consentv2', {
          ad_Storage: e.detail.marketingAllowed ? 'granted' : 'denied',
          analytics_Storage: e.detail.analyticsAllowed ? 'granted' : 'denied',
          source: 101,
        });
      });
    })(window, document, 'clarity', 'script', 'vy1aqjci0k');
  };
  ['scroll', 'pointerdown', 'keydown', 'touchstart'].forEach((ev) => addEventListener(ev, startClarity, { once: true, passive: true }));
  const clarityTimer = () => setTimeout(startClarity, 8000);
  if (document.readyState === 'complete') clarityTimer();
  else addEventListener('load', clarityTimer, { once: true });

  // ---- 3. inert hidden slides ----
  const syncSlide = (el) => {
    const hidden = el.getAttribute('aria-hidden') === 'true';
    if (el.inert !== hidden) el.inert = hidden;
  };
  // Watch only the slides themselves (attribute changes), never the whole document.
  const SLIDE = 'slideshow-slide, .resource-list__slide';
  const watched = new WeakSet();
  const mo = new MutationObserver((muts) => muts.forEach((m) => syncSlide(m.target)));
  const scan = () => document.querySelectorAll(SLIDE).forEach((el) => {
    syncSlide(el);
    if (!watched.has(el)) { watched.add(el); mo.observe(el, { attributes: true, attributeFilter: ['aria-hidden'] }); }
  });
  addEventListener('load', scan, { once: true });
  document.addEventListener('shopify:section:load', scan);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
})();
